// Renders formatting actions for the shared message editor.

import { Bold, Code, Italic, List, ListOrdered, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { EditorCommand, useMessageEditor } from './use-message-editor';

const FORMAT_ACTIONS: { command: EditorCommand; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
  { command: 'strong', icon: Bold, label: 'Bold' },
  { command: 'em', icon: Italic, label: 'Italic' },
  { command: 'code', icon: Code, label: 'Code' },
  { command: 'bulletList', icon: List, label: 'Bullet list' },
  { command: 'orderedList', icon: ListOrdered, label: 'Ordered list' },
];

export function FormatToolbar({ editor }: { editor: ReturnType<typeof useMessageEditor> }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-6 text-gray-900 hover:bg-gray-100 hover:text-text-heading"
          aria-label="Text formatting"
        >
          <Type className="size-[14px]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="flex w-auto items-center gap-1 rounded-full px-2 py-1.5">
        {FORMAT_ACTIONS.map(({ command, icon: Icon, label }) => (
          <Button
            key={command}
            variant="ghost"
            size="icon-sm"
            onClick={() => editor.exec(command)}
            className={cn(
              'size-6 text-gray-900 hover:bg-gray-100 hover:text-text-heading',
              editor.isActive(command) && 'bg-gray-100 text-text-heading',
            )}
            aria-label={label}
          >
            <Icon className="size-[14px]" />
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
